<template>
  <b-row class="time-grid-swiper-row d-flex flex-column mh-0" :class="{ 'h-100': isMonthView }">
    <b-col class="p-0 custom-pr d-flex flex-column mh-0 flex-grow-1 flex-shrink-1 flex-basis-auto">
      <swiper-container
        ref="timeGridSwiper"
        init="false"
        class="time-grid-swiper-container"
        :class="{ 'custom-swiper-container-month-view': isMonthView, 'swiper--transition': isTransitioning }"
        @swiperslidenexttransitionend="changePage(+1)"
        @swiperslideprevtransitionend="changePage(-1)"
        @swiperslidechangetransitionstart="handleTransitionStart"
        @swiperslidechangetransitionend="handleTransitionEnd"
        @swiperslidermove="handleTransitionStart(true)"
      >
        <swiper-slide v-for="(chunkUnixDate, weekIndex) in unixDates"
                      :key="chunkUnixDate"
                      class="swiper-slide"
                      data-cy="swiper-slide-item"
        >
          <div class="time-grid-swiper-wrapper">
            <slot name="time-grid-date-header" :chunk-unix-date="chunkUnixDate">
              <time-grid-date-header
                :key="chunkUnixDate + 1"
                :start-of-week-unix="chunkUnixDate"
                :is-disabled="isLoading"
                :hide-title="hideDateHeaderTitle"
                @date-header-switch-view-type="(targetViewType) => $emit('dateHeader-switch-view-type', targetViewType)"
              />
            </slot>

            <slot name="time-grid-all-day-row" :all-day-containers="allDayContainers[chunkUnixDate]" />

            <b-container
              :ref="weekIndex === 1 ? 'timeGridContainer' : 'timeGridContainerInactive'"
              fluid
              class="time-grid-container"
              :class="{ 'h-100 p-0 overflow-hidden': isMonthView }"
              data-test="time-grid-container"
              data-cy="time-grid-container"
            >
              <div v-if="isLoading" class="loading-background position-absolute w-100">
                <div class="bg-white position-absolute w-100 h-100 opacity-50" />
                <sdui-loader class="mt-5 p-3" data-test="sdui-loader" />
              </div>
              <slot
                v-if="isMonthView"
                name="time-grid-rows"
                :month-containers="monthContainers[chunkUnixDate] || {}"
                :chunk-unix-date="chunkUnixDate"
              />

              <b-row v-else data-test="day-row">
                <slot name="time-grid-left-column" />

                <b-col
                  v-for="(dayContainer, dayIndex) in weekContainers[chunkUnixDate]"
                  :key="dayIndex"
                  data-cy="day-outer"
                  data-test="day-outer"
                  class="p-0"
                >
                  <slot
                    name="time-grid-column"
                    :chunk-unix-date="chunkUnixDate"
                    :day-container="dayContainer"
                    :week-index="weekIndex"
                    :day-index="dayIndex"
                    :is-weekend="dayIndex > 4"
                  />

                  <div v-if="showOverlay"
                       data-test="overlay"
                       class="overlay"
                       :style="{ height: overlayClass[weekIndex][dayIndex] }"
                  />
                </b-col>
              </b-row>
            </b-container>
          </div>
        </swiper-slide>
      </swiper-container>
    </b-col>
  </b-row>
</template>

<script>
import TimeGridDateHeader from 'src/components/timeGrid/TimeGridDateHeader.vue'
import timeGridSwiping from 'src/mixins/timeGrid/timeGridSwiping.js'
import timeGridRendering from 'src/mixins/timeGrid/timeGridRendering.js'
import { TIME_GRID } from 'src/config/constants.ts'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'

export default {
  name: 'TimeGridSwiper',
  components: {
    SduiLoader,
    TimeGridDateHeader,
  },

  mixins: [timeGridSwiping, timeGridRendering],
  props: {
    initSwiperSynchronously: {
      type: Boolean,
      default:false
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    swiperOptions: {
      type: Object,
      default: () => TIME_GRID.DEFAULT_SWIPER_OPTIONS,
    },

    allItemsForSwiper: {
      type: Array,
      default: () => [],
    },

    customRecalculateFn: {
      type: Function,
      default: null,
    },

    timeFrame: {
      type: Object,
      required: true,
      validator: function (value) {
        return ['week_minutes_begins_at', 'week_minutes_ends_at'].every(
          (item) => value && Object.prototype.hasOwnProperty.call(value, item),
        )
      },
    },

    showOverlay: {
      type: Boolean,
      default: true,
    },

    isKeyboardSwipeDisabled: {
      type: Boolean,
      default: false,
    },

    showAllDayRow: {
      type: Boolean,
      default: false,
    },

    hideDateHeaderTitle: {
      type: Boolean,
      default: false,
    },

    dayCellHeight: {
      type: Number,
      default: null,
    },
  },

  data: () => ({
    weekContainers: {},
    allDayContainers: {},
    monthContainers: {},
    currentTime: null,
    isTransitioning: false,
  }),

  computed: {
    swiperTargetSlide() {
      if (this.swiperOptions?.initialSlide !== null && this.swiperOptions?.initialSlide !== undefined) {
        return this.swiperOptions?.initialSlide
      }
      return 1
    },

    overlayClass() {
      const now = this.$moment()
      return this.dates.map((startOfWeekDate) => {
        const daysOfWeek = new Array(this.daysToShow).fill(null)
        return daysOfWeek.map((_, i) => {
          const date = startOfWeekDate.clone().add(i, 'days')

          // past
          if (date.isBefore(now, 'day')) {
            return '100%'
          }

          // future
          if (date.isAfter(now, 'day')) {
            return '0%'
          }

          // today
          return this.overlayHeight
        })
      })
    },

    overlayHeight() {
      const time = this.currentTime
      const startTime = time.get('minute') + time.get('hour') * 60 - this.timeFrame.week_minutes_begins_at

      // avoid overflows
      if (startTime < 0) return

      return `${startTime * this.defaultColumnScale}px`
    },

    filteredAllItemsForSwiper() {
      if (!this.showAllDayRow) return this.allItemsForSwiper
      return this.allItemsForSwiper.filter((el) => !el.isAllDay)
    },

    allDayItemsForSwiper() {
      if (!this.showAllDayRow) return []
      return this.allItemsForSwiper.filter((el) => el.isAllDay)
    },

    maxEventsPerDay() {
      // 20px (TimeGridMonthRow.vue -> .cell-item) is the height of a single event,
      // 26px (TimeGridMonthRow.vue -> .day-caption) is the height of the day caption
      // -1 cellItem, so we are able to display "+N" button if needed
      return Math.floor((this.dayCellHeight - 26) / 20) - 1
    },
  },

  emits: ['recalculate-finished', 'fetch-next-chunk', 'dateHeader-switch-view-type'],

  methods: {
    async recalculateData(reset = false) {
      if (!this.customRecalculateFn) return this.defaultRecalculateData(reset)

      await this.customRecalculateData(reset)
    },

    async calculateAllDayEvents(reset) {
      if (!this.showAllDayRow) return {}
      let existingAllDayContainers = this.$clone(this.allDayContainers)
      const allDayItemRowsPerOverlap = await this.getItemRowsPerOverlap(
        this.allDayItemsForSwiper,
        this.startDate,
        this.dates,
        this.isDayView,
        this.daysToShow,
      )

      if (!reset && existingAllDayContainers) return {}

      existingAllDayContainers = {}
      allDayItemRowsPerOverlap.forEach((item, index) => {
        existingAllDayContainers[this.unixDates[index]] = item
      })
      return existingAllDayContainers
    },

    async calculateMonthEvents(reset) {
      const existingMonthContainers = this.$clone(this.monthContainers)

      const assignWeekUnixOfMonth = (monthUnix, overlappingItemRowsByMonth) => {
        const newMonthContainer = {}
        overlappingItemRowsByMonth.forEach((item, index) => {
          newMonthContainer[this.monthDates[monthUnix][index].unix()] = item
        })
        return newMonthContainer
      }

      // create promises for each month and map them
      const promises = this.unixDates.map(async (unixDate) => {
        // Month view starts with calendar month, which is not always the first day of the selected month.
        // The first child array of this.monthDates[unixDate] is always the date where the calendar month starts.
        const firstWeekOfCalendarMonthUnix = this.monthDates[unixDate][0]
        const itemRows = await this.getItemRowsPerOverlap(
          this.allItemsForSwiper,
          firstWeekOfCalendarMonthUnix,
          this.monthDates[unixDate],
          false,
          TIME_GRID.MAX_DAYS_TO_SHOW,
          this.maxEventsPerDay,
        )
        return [unixDate, itemRows]
      })

      // resolve promises and create object
      const overlappingItemRowsByMonth = Object.fromEntries(await Promise.all(promises))

      // go through each month and assign the week unix of each week of a month
      for (const [unixDate, itemRows] of Object.entries(overlappingItemRowsByMonth)) {
        overlappingItemRowsByMonth[unixDate] = assignWeekUnixOfMonth(unixDate, itemRows)
      }

      return this.getPerformantContainers(reset, existingMonthContainers, overlappingItemRowsByMonth).reduce(
        (o, key, i) => ({ ...o, [this.unixDates[i]]: key }),
        {},
      )
    },

    async calculateWeekAndDayEvents(reset) {
      const existingWeekContainers = this.$clone(this.weekContainers)
      const lessonsByWeekAndDay = await this.getItemsByWeekAndDay(
        this.filteredAllItemsForSwiper,
        this.startDate,
        this.dates,
        this.isWeekView,
        this.daysToShow,
      )
      const weekContainers = await this.getWeekContainers(lessonsByWeekAndDay)

      return this.getPerformantContainers(reset, existingWeekContainers, weekContainers).reduce(
        (o, key, i) => ({ ...o, [this.unixDates[i]]: key }),
        {},
      )
    },

    getPerformantContainers(reset, existingContainers, containers) {
      return this.unixDates.map((date, i) => {
        // reuse existing containers if they are already created
        // to reduce object changing and recalculation
        if (!reset && existingContainers[date]) return existingContainers[date]

        return containers[this.isMonthView ? date : i]
      })
    },

    async defaultRecalculateData(reset = false) {
      if (this.isMonthView) {
        this.monthContainers = await this.calculateMonthEvents(reset)
      } else {
        this.allDayContainers = await this.calculateAllDayEvents(reset)
        this.weekContainers = await this.calculateWeekAndDayEvents(reset)
      }
      this.recalculateFinished()
    },

    async customRecalculateData(reset = false) {
      const { weekContainers } = await this.customRecalculateFn(reset)
      this.weekContainers = weekContainers

      this.recalculateFinished()
    },

    recalculateFinished() {
      if (this.$refs?.timeGridSwiper?.swiper) {
        this.$refs.timeGridSwiper.swiper.updateActiveIndex(this.swiperTargetSlide)
        this.$refs.timeGridSwiper.swiper.update()
      }
      this.$emit('recalculate-finished')
    },

    updateTime() {
      this.currentTime = this.$moment()
    },

    handleTransitionStart(onSwipe = false) {
      this.isTransitioning = true

      if (onSwipe) this.saveScrollPosition()

      // ensure the non-active slides are scrolled to the last scroll position, when entering the screen (to prevent flicker on transitionEnd)
      const timeGridContainers = [...document.querySelectorAll('.swiper-slide:not(.swiper-slide-active) .time-grid-container')]
      timeGridContainers.forEach((timeGridContainer) => {
        timeGridContainer.scrollTop = this.lastScrollPosition
      })
    },

    async handleTransitionEnd() {
      this.isTransitioning = false
      this.$emit('fetch-next-chunk')

      // restore last scrollPosition
      await this.$nextTick()
      setTimeout(() => {
        const timeGridContainer = this.$refs.timeGridContainer[0]
        if (timeGridContainer) timeGridContainer.scrollTop = this.lastScrollPosition
      }, 0)
    },

    initSwiper() {
      if (!this.$refs.timeGridSwiper) return

      Object.assign(this.$refs.timeGridSwiper, this.swiperOptions)

      this.$refs.timeGridSwiper.initialize()
    },
  },

  watch: {
    // This will also be triggered if the viewType was changed because this action changes the unixDates
    unixDates: {
      handler(newValue, oldValue) {
        // Swiping in dayView changes the startDate (single date instead of start of the week) which in turn changes unixDates.
        // Executing the removeWeek logic below would remove the wrong items. A recalculate will still happen because of
        // the 'allItemsForSwiper' watcher.
        if (this.$compare(oldValue, newValue)) return
        this.recalculateData(true)
      },

      deep: true,
    },

    allItemsForSwiper: {
      handler(newVal, oldVal) {
        if (this.$compare(oldVal, newVal)) return
        this.recalculateData(true)
      },
    },

    daysToShow: {
      handler(newVal, oldVal) {
        if (this.$compare(oldVal, newVal) || this.isMonthView) return
        this.recalculateData(true)
      },
    },

    dayCellHeight: {
      handler(newVal, oldVal) {
        if (this.$compare(oldVal, newVal)) return
        this.recalculateData(true)
      },
    },

    viewType: {
      handler(newVal, oldVal) {
        if (oldVal === newVal) return
        this.monthContainers = {}
      },
    },
  },

  mounted() {
    this.currentTime = this.$moment()
    window.addEventListener('focus', this.updateTime)
    // Wait for all height calculations to finish on sdui-planner
    // then init swiper
    if (this.initSwiperSynchronously) {
      return this.recalculateData(true).then(() => {
        this.initSwiper()
      })
    }
    this.recalculateData(true)
    this.initSwiper()
  },

  created() {
    document.addEventListener('keydown', this.keyboardSwipe)
  },

  unmounted() {
    document.removeEventListener('keydown', this.keyboardSwipe)
  },

  beforeUnmount() {
    window.removeEventListener('focus', this.updateTime)
  },
}
</script>

<style scoped lang="scss">
// Prevent keyboard+screenreader from focusing hidden slides
.time-grid-swiper-container:not(.swiper--transition) {
  .swiper-slide-prev .time-grid-swiper-wrapper,
  .swiper-slide-next .time-grid-swiper-wrapper {
    display: none;
  }
}

.time-grid-swiper-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;
  height: auto;
  width: 100%;
}

.loading-background {
  height: inherit;
  left: 0;
  z-index: 5;

  .custom-spinner {
    padding-top: 85px !important;
  }
}

.custom-pr {
  padding-right: 2px !important;
}

.time-grid-swiper-container {
  overflow: visible;
  display: flex;
  flex-direction: row;
  height: auto;

  // This fixes the swiper to correctly calculate width/height
  // (stolen from their github)
  width: 100%;
  max-width: 100%;
  min-height: 0;
  min-width: 0;
}

.swiper-slide {
  height: auto !important;
  display: flex;
  min-height: 0;
  min-width: 0;
}

.time-grid-container {
  min-height: 600px;
  padding-bottom: 0;
}

.overlay {
  background: rgba($color-black-rgb, 0.1);
  z-index: 20;
  position: absolute;
  top: 0px;
  width: 100%;
  user-select: none;
  pointer-events: none;
  max-height: 100%;
}

.custom-swiper-container-month-view {
  height: 100% !important;
}

// iOS only
@supports (-webkit-touch-callout: none) {
  .custom-swiper-container-month-view {
    height: -webkit-fill-available !important;
  }
}
</style>
